import type { GiftTypeEnum } from '@sola/schema/dotcom/cart/gift.ts';

export const MY_API_BASE = `${process.env.NEXT_PUBLIC_BASE_URL_MY || ''}/api`;

export const MY_QUERY_KEYS = {
  childDetails: 'childDetails',
  childAvailability: 'childAvailability',
  sameCenterChildAvailability: 'sameCenterChildAvailability',
} as const;

export const knownMyUrls = {
  billingSettings: '/billing/settings/',
  billingTaxReceipts: '/billing/tax-receipts/',
  billingCommitments: '/billing/commitments/',
  givingChildGifts: '/giving/child-gifts/',
  homepage: '/',
  settingsContact: '/settings/contacts/',
  sponsorshipsBase: '/sponsorships/',
  sponsorshipsNew: '/sponsorships/new/',
};

const getWriteBaseUrl = (): string =>
  process.env.NEXT_PUBLIC_BASE_URL_WRITE || 'https://write.compassion.com';

export const getWriteSponsorshipPath = (beneficiaryGlobalId: string): string =>
  `${getWriteBaseUrl().replace(/\/$/, '')}/sponsorships/${beneficiaryGlobalId}/write`;

const dotcomBase =
  process.env.NEXT_PUBLIC_BASE_URL_DOTCOM || 'https://www.compassion.com';
const dotcomCatalog = `${dotcomBase}/catalog`;

// TODO: These should really come from knownSkippyUrls, no?
export const knownDotcomUrls = {
  catalog: `${dotcomCatalog}/`,
  catalogBibles: `${dotcomCatalog}/bibles/`,
  catalogChickens: `${dotcomCatalog}/chickens/`,
  catalogGardenSeeds: `${dotcomCatalog}/vegetable-seeds/`,
  catalogGoats: `${dotcomCatalog}/goats/`,
  catalogWaterWells: `${dotcomCatalog}/water-wells/`,
  christmasGift: `${dotcomBase}/give/donate/christmas-gift/`,
  compassionSunday: `${dotcomBase}/get-involved/compassion-sunday/`,
  sponsorAChild: `${dotcomBase}/sponsor-a-child/`,
};

export const knownExternalUrls = {
  christmasCards: 'https://compassion.donordrive.com/events/738',
};

export const myQueryParams = {
  childBio: {
    childGlobalId: 'gid',
    subAccepted: 'subAccepted',
  },
  childGifts: {
    childId: 'id',
    giftType: 'type',
  },
  commitments: {
    activeItemId: 'id',
  },
} as const;

/**
 * Generates the child gifts path. Both values are required to preselect a child
 * and gift type; otherwise, the base child gifts path is returned.
 * @param globalId - The child's global identifier.
 * @param giftType - The gift type to preselect.
 */
export const getGivingChildGiftsPath = (
  globalId?: string | null,
  giftType?: GiftTypeEnum | null,
): string => {
  if (!globalId || !giftType) {
    return knownMyUrls.givingChildGifts;
  }

  const queryParams = new URLSearchParams({
    [myQueryParams.childGifts.childId]: globalId,
    [myQueryParams.childGifts.giftType]: giftType.toLowerCase(),
  });

  return `${knownMyUrls.givingChildGifts}?${queryParams.toString()}`;
};

/**
 * Generates the child bio path.
 * @param globalId - The child's global identifier.
 */
export const getChildBioPath = (globalId?: string | null): string => {
  if (!globalId) {
    return knownMyUrls.sponsorshipsBase;
  }

  return `${knownMyUrls.sponsorshipsBase}${globalId}/`;
};

/**
 * Generates the child bio path with a departure substitute confirmation.
 * @param acceptedChildGlobalId - The accepted substitute child's global identifier.
 */
export const getChildBioPathWithSubAccepted = (
  acceptedChildGlobalId: string,
): string => {
  const queryParams = new URLSearchParams({
    [myQueryParams.childBio.subAccepted]: 'true',
  });

  return `${getChildBioPath(acceptedChildGlobalId)}?${queryParams.toString()}`;
};

/**
 * Generates the billing commitment sheet link.
 */
export const getBillingCommitmentPath = (commitmentId: string): string =>
  `${knownMyUrls.billingCommitments}?${myQueryParams.commitments.activeItemId}=${commitmentId}`;

/**
 * Generates the departure path for a sponsored child.
 * @param globalId - The departed child's global identifier.
 */
export const getDeparturePath = (globalId: string): string =>
  `${getChildBioPath(globalId)}leaving/`;

/**
 * Generates the offered substitute child path for a departure.
 * @param globalId - The departed child's global identifier.
 */
export const getDepartureSubPath = (globalId: string): string =>
  `${getDeparturePath(globalId)}match/`;

/**
 * Generates the path for reviewing an alternate substitute child.
 * @param departedChildGlobalId - The departed sponsored child's identifier.
 * @param replacementChildGlobalId - The alternate child's identifier.
 */
export const getDepartureSubAlternatePath = (
  departedChildGlobalId: string,
  replacementChildGlobalId: string,
): string =>
  `${getDepartureSubPath(departedChildGlobalId)}${encodeURIComponent(replacementChildGlobalId)}/`;
